import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { catchError, forkJoin, Observable, of, switchMap, throwError } from 'rxjs';
import { PatchScandataModel, PatchScandataModels } from '../scandata/scandata.actions';
import {
  PointcloudType,
  ScandataDisplayStatus,
  ScandataLoadStatus,
  ScandataModel,
  ScandataSaveStatus,
} from '../scandata/scandata.models';
import { ScandataService } from '../scandata/scandata.service';
import { StationTransform } from '../station/station-transform.models';
import { ClearCurrentStation, SetCurrentStation } from '../station/station.actions';
import { Station, StationDisplayStatus, StationLoadStatus } from '../station/station.models';
import { StationService } from '../station/station.service';
import { StationState } from '../station/station.state';
import { TilesetStatus } from '../tileset/tileset.models';
import { TilesetService } from '../tileset/tileset.service';

@Injectable({
  providedIn: 'root',
})
export class Host3dService {
  private readonly scandataService = inject(ScandataService);
  private readonly stationService = inject(StationService);
  private readonly store = inject(Store);
  private readonly tilesetService = inject(TilesetService);

  showScan(scan: ScandataModel) {
    return forkJoin([this.loadModel(scan), this.loadScanStations(scan)]);
  }

  hideScan(scan: ScandataModel) {
    return this.store.dispatch(
      new PatchScandataModel({
        id: scan.id,
        displayStatus: ScandataDisplayStatus.AwaitingHide,
        showInScene: false,
      }),
    );
  }

  hideScans(scans: ScandataModel[]) {
    const modelsToPatch = scans.map((scan) => ({
      id: scan.id,
      displayStatus: ScandataDisplayStatus.AwaitingHide,
      showInScene: false,
    }));

    return this.store.dispatch(new PatchScandataModels(modelsToPatch));
  }

  loadScanStations(scan: ScandataModel): Observable<boolean | void> {
    if (scan.stationLoadStatus === StationLoadStatus.Loading) return of(false);

    // TODO: Remove once imaging service stations are supported
    if (scan.type === PointcloudType.MobileMappingRun) return of(false);

    return this.store
      .dispatch(
        new PatchScandataModel({
          id: scan.id,
          stationLoadStatus: StationLoadStatus.Loading,
        }),
      )
      .pipe(
        switchMap(() => this.stationService.getStations(scan.id)),
        switchMap((stations) =>
          this.store.dispatch(
            new PatchScandataModel({
              id: scan.id,
              stations,
              stationLoadStatus: StationLoadStatus.Loaded,
            }),
          ),
        ),
        catchError(() =>
          this.store.dispatch(
            new PatchScandataModel({
              id: scan.id,
              stationLoadStatus: StationLoadStatus.LoadingError,
            }),
          ),
        ),
      );
  }

  setCurrentStation(station: Station) {
    const currentStation$ = this.store.selectOnce(StationState.currentStation);

    return currentStation$.pipe(
      switchMap((currentStation) =>
        currentStation?.station.id === station.id
          ? this.store.dispatch(new ClearCurrentStation())
          : this.store.dispatch(
              new SetCurrentStation({
                station,
                displayStatus: StationDisplayStatus.AwaitingDisplay,
              }),
            ),
      ),
    );
  }

  updateStationTransform(scanId: string, stationTransform: StationTransform): Observable<void> {
    return this.store
      .dispatch(new PatchScandataModel({ id: scanId, saveStatus: ScandataSaveStatus.Saving }))
      .pipe(
        switchMap(() =>
          this.scandataService
            .updateScandataModel({
              id: scanId,
              stationTransform,
            })
            .pipe(
              switchMap(() =>
                this.store.dispatch(
                  new PatchScandataModel({ id: scanId, saveStatus: ScandataSaveStatus.Saved }),
                ),
              ),
              catchError((error) =>
                this.store
                  .dispatch(
                    new PatchScandataModel({
                      id: scanId,
                      saveStatus: ScandataSaveStatus.SaveError,
                    }),
                  )
                  .pipe(switchMap(() => throwError(() => error))),
              ),
            ),
        ),
      );
  }

  private loadModel(scan: ScandataModel) {
    return this.store
      .dispatch(
        new PatchScandataModel({
          id: scan.id,
          loadStatus: ScandataLoadStatus.Loading,
        }),
      )
      .pipe(
        switchMap(() => this.tilesetService.getTilesets(scan.id)),
        switchMap((tilesets) =>
          this.store.dispatch(
            new PatchScandataModel({
              id: scan.id,
              tilesets,
              loadStatus: tilesets.some((t) => t.status !== TilesetStatus.Ready)
                ? ScandataLoadStatus.LoadingError
                : ScandataLoadStatus.Loaded,
              displayStatus: ScandataDisplayStatus.Hidden,
              showInScene: true,
            }),
          ),
        ),
        catchError(() =>
          this.store.dispatch(
            new PatchScandataModel({
              id: scan.id,
              loadStatus: ScandataLoadStatus.LoadingError,
            }),
          ),
        ),
      );
  }
}
